import { CURRENCY } from '@cxc/core/enums';

import { CurrencifyPipe } from '@cxc/ui-kit/pipes';

import { hasDefinedCurrency } from '@cxc/core/utils';

const currencify = new CurrencifyPipe();

/**
 * What a monetary KPI shows when it has no amount (LEN-2460 scenario 6): a
 * dash, never `$0.00`, which reads as a real zero.
 */
export const MISSING_AMOUNT = '—';

/**
 * An amount as the text a portfolio KPI shows, through the library's
 * `currencify` so the per-currency format (es-CO for COP, LEN-2460) arrives
 * with the @cxc/ui-kit bump instead of being re-implemented here (LEN-2464).
 *
 * Shared by Facility Insights and Overview (MR !1265 review, S1). A missing
 * or non-finite amount renders `MISSING_AMOUNT` (LEN-2465): `currencify`
 * hands those values back untouched, so the card would otherwise print
 * "null" or "NaN" -- and coercing them to zero, as this did before, printed a
 * zero nobody reported. A real `0` still renders as `$0.00` / `$0,00`.
 */
export function formatMoney(
  value: number | null | undefined,
  currency: CURRENCY
): string {
  if (value == null || !Number.isFinite(value)) {
    return MISSING_AMOUNT;
  }

  return String(currencify.transform(value, currency));
}

/**
 * An API currency code narrowed to one the formatters know (LEN-2465). The
 * payloads type it as a free-form string -- and `OUT_OF_STANDARD_CURRENCY`
 * allows `L`/`EU`, which `CURRENCY` does not cover -- so anything else falls
 * back to the caller's currency instead of rendering an amount with no
 * symbol.
 */
export function knownCurrencyOr(
  code: string | null | undefined,
  fallback: CURRENCY
): CURRENCY {
  return hasDefinedCurrency(code) ? code : fallback;
}
